/**
 * Reportes → Ventas por cliente y descuentos (`/reportes/ventas-descuentos`).
 *
 * Puerto de `Reportes::ventas_por_cliente`, `Reportes::reporte_descuento_cliente`,
 * `Reportes::reporte_descuento_periodo` y `Reportes::reporte_descuento_periodo_interno`
 * + `Reportes_model::get_all_documents_by_period`,
 * `get_comprobante_by_periodo_descuento` y `get_internal_document_by_period` +
 * sus cuatro vistas. Las cuatro pantallas del panel anterior compartían consulta
 * y sólo cambiaban el filtro de **fuente** y **tipo de descuento**, así que aquí
 * son **una sola pantalla** con esos filtros y con el enlace del centro
 * (`?descuento=1&familia=…`) prefijando los suyos.
 *
 * Columnas del original: tipo, serie, fecha, cliente, placa, producto, cantidad,
 * precio unidad, total, descuento, grifero y estado; el pie sumaba cantidad,
 * total y descuento. Las filas de un comprobante **anulado** se veían con sus
 * importes en cero, para que no sumaran al pie.
 *
 * Reglas conservadas:
 * - Se listan las líneas de las facturas, boletas, notas de venta y ventas al
 *   crédito del rango, con el descuento de la línea (`venta_producto.sub_descuento`
 *   y, en el crédito, `credito_venta.descuento` por la cantidad).
 * - El importe de un comprobante anulado va en cero y la fila se marca como
 *   anulada; el pie suma cantidad, base, total y descuento.
 * - El filtro «sólo con descuento» deja fuera las líneas sin descuento (y, como
 *   en el original, también las anuladas, porque su descuento queda en cero).
 * - Si una línea no tiene ajuste guardado, se recupera la regla activa de playa
 *   o crédito como hacía el reporte anterior.
 *
 * Diferencias anotadas:
 * - La consulta del original pedía `estado_api IN ('Aceptado','Registrado','ANULADO')`
 *   para facturas y boletas, así que las **boletas pendientes de envío** (estado
 *   nulo) no salían; aquí sí, como «Pendiente», y los anulados se reconocen con
 *   `LIKE 'Anulado%'`, que también cubre «Anulado por NC».
 * - Se añade la vista **por cliente** y **por producto** (las mismas líneas
 *   agrupadas), el atajo por **meses con comprobantes** y el CSV servido por la
 *   API.
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosVentasDescuentos,
  useVentasDescuentos,
  type FiltrosVentasDescuentos,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/** Métodos de pago de la venta que se pueden pedir como preset (`?metodo_pago=`). */
const METODOS_PAGO = ['CONTADO', 'IZIPAY', 'TRANSFERENCIA', 'BONUS'];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const formatLegacyNumber = (value: number | null | undefined): string =>
  formatNumber(Number(Number(value ?? 0).toFixed(2)));

const formatLegacyCurrency = (value: number | null | undefined): string =>
  formatCurrency(Number(Number(value ?? 0).toFixed(2)));

const VISTAS = [
  { valor: 'lineas', etiqueta: 'Detalle de líneas' },
  { valor: 'clientes', etiqueta: 'Por cliente' },
] as const;

type Vista = (typeof VISTAS)[number]['valor'];

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto: el mes en curso (como el panel anterior). */
function rangoPorDefecto(): { desde: string; hasta: string } {
  const hoy = new Date();

  return {
    desde: `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`,
    hasta: fechaLocal(hoy),
  };
}

/** «agosto 2026» a partir de `2026-08`. */
function etiquetaMes(periodo: string | null | undefined): string {
  if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) return periodo ?? '';

  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

/** Primer y último día de un mes `AAAA-MM`. */
function mesComoRango(periodo: string): { desde: string; hasta: string } {
  const anio = Number(periodo.slice(0, 4));
  const mes = Number(periodo.slice(5, 7));

  return {
    desde: `${periodo}-01`,
    hasta: fechaLocal(new Date(anio, mes, 0)),
  };
}

/** Tono del badge según el tipo de comprobante. */
function tonoDelTipo(tipo: string): 'brand' | 'neutral' | 'warning' | 'danger' {
  if (tipo === 'FACTURA') return 'brand';
  if (tipo === 'BOLETA') return 'neutral';
  if (tipo === 'CRÉDITO') return 'warning';

  return 'danger';
}

export function ReporteVentasDescuentosPage() {
  useDocumentTitle('Reportes · Ventas por cliente y descuentos');

  const [parametros, setParametros] = useSearchParams();
  const [filtros, setFiltros] = useState<FiltrosVentasDescuentos>(() => ({
    ...rangoPorDefecto(),
    numero_doc: '',
    familia: parametros.get('familia') ?? 'todos',
    fuentes: [],
    tipos: [],
    solo_descuentos: parametros.get('descuento') === '1',
    activos: parametros.get('activos') === '1',
    extras: parametros.get('extras') === '1',
    metodo_pago: parametros.get('metodo_pago') ?? '',
  }));
  const [vista, setVista] = useState<Vista>(
    parametros.get('vista') === 'clientes' ? 'clientes' : 'lineas',
  );
  const [aviso, setAviso] = useState<string | null>(null);

  // El centro de reportes abre cada ficha con sus filtros en la URL.
  const descuentoDeLaUrl = parametros.get('descuento') === '1';
  const activosDeLaUrl = parametros.get('activos') === '1';
  const extrasDeLaUrl = parametros.get('extras') === '1';
  const metodoDeLaUrl = parametros.get('metodo_pago') ?? '';
  const familiaDeLaUrl = parametros.get('familia') ?? 'todos';
  const vistaDeLaUrl: Vista = parametros.get('vista') === 'clientes' ? 'clientes' : 'lineas';

  useEffect(() => {
    setFiltros((actual) =>
      actual.solo_descuentos === descuentoDeLaUrl &&
      actual.activos === activosDeLaUrl &&
      actual.extras === extrasDeLaUrl &&
      actual.metodo_pago === metodoDeLaUrl &&
      actual.familia === familiaDeLaUrl
        ? actual
        : {
            ...actual,
            solo_descuentos: descuentoDeLaUrl,
            activos: activosDeLaUrl,
            extras: extrasDeLaUrl,
            metodo_pago: metodoDeLaUrl,
            familia: familiaDeLaUrl,
          },
    );
    setVista((actual) => (actual === vistaDeLaUrl ? actual : vistaDeLaUrl));
  }, [activosDeLaUrl, descuentoDeLaUrl, extrasDeLaUrl, familiaDeLaUrl, metodoDeLaUrl, vistaDeLaUrl]);

  const rango = useMemo(() => ({ desde: filtros.desde, hasta: filtros.hasta }), [filtros.desde, filtros.hasta]);
  const catalogos = useCatalogosVentasDescuentos(rango);
  const consulta = useVentasDescuentos(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const meses = catalogos.data?.meses ?? [];
  const clientes = catalogos.data?.clientes ?? [];
  const porCliente = datos?.por_cliente ?? [];
  const porProducto = datos?.por_producto ?? [];
  const lineas = datos?.lineas ?? [];
  // El listado del mes puede traer miles de líneas: se pagina en el navegador y el pie
  // sigue sumando el listado completo.
  const paginacion = usePaginacion(lineas, { clave: filtros });

  const cambiar = <C extends keyof FiltrosVentasDescuentos>(
    campo: C,
    valor: FiltrosVentasDescuentos[C],
  ) => setFiltros((previos) => ({ ...previos, [campo]: valor }));

  /** Cambiar familia, descuento, vigentes, extras, método o vista también lo deja en la URL. */
  const cambiarConUrl = (
    cambios: Partial<
      Pick<FiltrosVentasDescuentos, 'familia' | 'solo_descuentos' | 'activos' | 'extras' | 'metodo_pago'>
    >,
    nuevaVista?: Vista,
  ) => {
    const siguiente = { ...filtros, ...cambios };
    const siguienteVista = nuevaVista ?? vista;

    setFiltros(siguiente);
    if (nuevaVista !== undefined) setVista(nuevaVista);

    const parametrosNuevos: Record<string, string> = {};
    if (siguiente.solo_descuentos) parametrosNuevos.descuento = '1';
    if (siguiente.activos) parametrosNuevos.activos = '1';
    if (siguiente.extras) parametrosNuevos.extras = '1';
    if (siguiente.metodo_pago) parametrosNuevos.metodo_pago = siguiente.metodo_pago;
    if (siguiente.familia !== 'todos') parametrosNuevos.familia = siguiente.familia;
    if (siguienteVista !== 'lineas') parametrosNuevos.vista = siguienteVista;

    setParametros(parametrosNuevos, { replace: true });
  };

  const alternarFuente = (fuente: string, marcada: boolean) =>
    setFiltros((previos) => ({
      ...previos,
      fuentes: marcada
        ? [...previos.fuentes, fuente]
        : previos.fuentes.filter((valor) => valor !== fuente),
    }));

  const alternarTipo = (tipo: string, marcado: boolean) =>
    setFiltros((previos) => ({
      ...previos,
      tipos: marcado ? [...previos.tipos, tipo] : previos.tipos.filter((valor) => valor !== tipo),
    }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarVentasDescuentos(filtros), {
        nombre: `ventas-descuentos-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfVentasDescuentos(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  const etiquetaFamilia =
    catalogos.data?.familias.find((familia) => familia.valor === filtros.familia)?.etiqueta ?? '';

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">
            {filtros.extras
              ? 'Extras en ventas'
              : filtros.metodo_pago
                ? `Ventas con pago ${filtros.metodo_pago}`
                : filtros.solo_descuentos
                  ? 'Descuentos y recargos'
                  : 'Ventas por cliente'}
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            {filtros.extras
              ? 'Líneas vendidas que no son combustible (lubricantes y combos), con su comprobante y su grifero.'
              : 'Líneas vendidas del periodo con su descuento, por comprobante y por cliente.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-ink-700">
                Cliente{' '}
                <span className="font-normal text-ink-400">
                  {catalogos.data
                    ? `(${formatNumber(clientes.length)} con ventas en el periodo)`
                    : ''}
                </span>
              </span>
              <Select
                value={filtros.numero_doc}
                onChange={({ target }) => cambiar('numero_doc', target.value)}
                className={CONTROL}
                aria-label="Cliente"
              >
                <option value="">TODOS</option>
                {clientes.map((cliente) => (
                  <option key={cliente.documento ?? cliente.cliente} value={cliente.documento ?? ''}>
                    {cliente.cliente} · {cliente.documento ?? 'sin documento'} (
                    {formatNumber(cliente.total)})
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Comprobantes</span>
              <Select
                value={filtros.familia}
                onChange={({ target }) => cambiarConUrl({ familia: target.value })}
                className={CONTROL}
                aria-label="Familia de comprobantes"
              >
                {(catalogos.data?.familias ?? []).map((familia) => (
                  <option key={familia.valor} value={familia.valor}>
                    {familia.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <div className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Fuente y tipo de descuento</span>
              <div className="flex flex-wrap items-center gap-1.5">
                {(catalogos.data?.fuentes ?? []).map((fuente) => {
                  const marcada = filtros.fuentes.includes(fuente.valor);

                  return (
                    <button
                      key={fuente.valor}
                      type="button"
                      aria-pressed={marcada}
                      onClick={() => alternarFuente(fuente.valor, !marcada)}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                        marcada
                          ? 'border-brand-300 bg-brand-50 text-brand-700'
                          : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                      )}
                    >
                      {fuente.etiqueta}
                    </button>
                  );
                })}

                {(catalogos.data?.tipos ?? []).map((tipo) => {
                  const marcado = filtros.tipos.includes(tipo.valor);

                  return (
                    <button
                      key={tipo.valor}
                      type="button"
                      aria-pressed={marcado}
                      onClick={() => alternarTipo(tipo.valor, !marcado)}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                        marcado
                          ? 'border-brand-300 bg-brand-50 text-brand-700'
                          : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                      )}
                    >
                      {tipo.etiqueta}
                    </button>
                  );
                })}

                {filtros.fuentes.length > 0 || filtros.tipos.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setFiltros((previos) => ({ ...previos, fuentes: [], tipos: [] }))}
                    className="ml-1 text-xs font-semibold text-ink-500 hover:text-brand-700 hover:underline"
                  >
                    Limpiar
                  </button>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Método de pago</span>
              <Select
                value={filtros.metodo_pago}
                onChange={({ target }) => cambiarConUrl({ metodo_pago: target.value })}
                className={CONTROL}
                aria-label="Método de pago"
              >
                <option value="">TODOS</option>
                {METODOS_PAGO.map((metodo) => (
                  <option key={metodo} value={metodo}>
                    {metodo}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Productos</span>
              <label className="inline-flex h-11 items-center gap-2 text-xs font-semibold text-ink-700">
                <input
                  type="checkbox"
                  className="size-4 accent-brand-600"
                  checked={filtros.extras}
                  onChange={(evento) => cambiarConUrl({ extras: evento.target.checked })}
                />
                Sólo extras (sin combustible)
              </label>
            </div>

            <div className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-ink-700">Vista</span>
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex overflow-hidden rounded-control border border-ink-200">
                  {VISTAS.map((opcion) => (
                    <button
                      key={opcion.valor}
                      type="button"
                      aria-pressed={vista === opcion.valor}
                      onClick={() => cambiarConUrl({}, opcion.valor)}
                      className={cn(
                        'px-3 py-2 text-xs font-semibold transition-colors',
                        vista === opcion.valor
                          ? 'bg-brand-50 text-brand-700'
                          : 'bg-white text-ink-600 hover:text-brand-700',
                      )}
                    >
                      {opcion.etiqueta}
                    </button>
                  ))}
                </div>

                <label className="inline-flex items-center gap-2 text-xs font-semibold text-ink-700">
                  <input
                    type="checkbox"
                    className="size-4 accent-brand-600"
                    checked={filtros.solo_descuentos}
                    onChange={(evento) =>
                      cambiarConUrl({ solo_descuentos: evento.target.checked })
                    }
                  />
                  Sólo líneas con descuento
                </label>

                <label className="inline-flex items-center gap-2 text-xs font-semibold text-ink-700">
                  <input
                    type="checkbox"
                    className="size-4 accent-brand-600"
                    checked={filtros.activos}
                    onChange={(evento) => cambiarConUrl({ activos: evento.target.checked })}
                  />
                  Sólo comprobantes vigentes
                </label>
              </div>
            </div>
          </div>

          {meses.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
              <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                <Search className="size-3.5" aria-hidden="true" />
                Meses con comprobantes
              </span>
              {meses.slice(0, 12).map((mes) => {
                const rango2 = mesComoRango(mes.periodo);
                const activo = rango2.desde === filtros.desde && rango2.hasta === filtros.hasta;

                return (
                  <button
                    key={mes.periodo}
                    type="button"
                    aria-pressed={activo}
                    onClick={() => setFiltros((previos) => ({ ...previos, ...rango2 }))}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                      activo
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                    )}
                    title={`${formatNumber(mes.comprobantes)} comprobante(s)`}
                  >
                    {etiquetaMes(mes.periodo)}
                  </button>
                );
              })}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      {resumen && resumen.lineas === 0 && porCliente.length === 0 ? (
        <Alert variant="info" title="El periodo elegido no tiene ventas">
          <p>
            Esta copia tiene comprobantes entre {etiquetaMes(catalogos.data?.rango.desde)} y{' '}
            {etiquetaMes(catalogos.data?.rango.hasta)}.
          </p>
          {meses[0] ? (
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => {
                const primero = meses[0];
                if (!primero) return;

                setFiltros((previos) => ({ ...previos, ...mesComoRango(primero.periodo) }));
              }}
            >
              Ver {etiquetaMes(meses[0]?.periodo)}
            </Button>
          ) : null}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {[
          {
            etiqueta: 'Líneas',
            valor: formatNumber(resumen?.lineas ?? 0),
            detalle: `${formatNumber(resumen?.comprobantes ?? 0)} comprobante(s)`,
          },
          {
            etiqueta: 'Total vendido',
            valor: formatCurrency(resumen?.total ?? 0),
            detalle: `base ${formatNumber(resumen?.subtotal ?? 0)}`,
          },
          {
            etiqueta: 'Descuentos',
            valor: formatCurrency(resumen?.descuento ?? 0),
            detalle: `${formatNumber(
              resumen && resumen.total > 0 ? (resumen.descuento / resumen.total) * 100 : 0,
            )} % de la venta`,
          },
          {
            etiqueta: 'Clientes',
            valor: formatNumber(resumen?.clientes ?? 0),
            detalle: 'con líneas en el periodo',
          },
          {
            etiqueta: 'Cantidad',
            valor: formatLegacyNumber(resumen?.cantidad ?? 0),
            detalle: `${formatNumber(resumen?.anuladas ?? 0)} línea(s) anulada(s)`,
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {vista === 'clientes' ? (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-ink-800">
                Ventas por cliente ({formatNumber(porCliente.length)})
              </h2>
              <span className="text-xs text-ink-500">
                {etiquetaFamilia}
                {filtros.solo_descuentos ? ' · sólo con descuento' : ''}
              </span>
            </div>

            {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

            {!consulta.isLoading ? (
              <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
                <table className="w-full max-lg:min-w-[34rem] table-fixed border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-1.5 py-2">Cliente</th>
                      <th className="w-[6rem] px-1.5 py-2 text-right">Comprobantes</th>
                      <th className="w-[5rem] px-1.5 py-2 text-right">Líneas</th>
                      <th className="w-[5.5rem] px-1.5 py-2 text-right">Cantidad</th>
                      <th className="w-[6.5rem] px-1.5 py-2 text-right">Total</th>
                      <th className="w-[6.5rem] px-1.5 py-2 text-right">Descuento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porCliente.length === 0 ? (
                      <tr className="border-t border-ink-100">
                        <td colSpan={6} className="px-1.5 py-10 text-center text-sm text-ink-500">
                          No hay ventas que cumplan los filtros.
                        </td>
                      </tr>
                    ) : null}

                    {porCliente.map((fila) => (
                      <tr
                        key={fila.documento ?? fila.cliente}
                        className="border-t border-ink-100 hover:bg-brand-50/30"
                      >
                        <td className="px-1.5 py-2">
                          <p className="truncate font-medium text-ink-800" title={fila.cliente}>
                            {fila.cliente}
                          </p>
                          <p className="truncate text-[11px] text-ink-500">
                            {fila.documento ?? 'sin documento'}
                          </p>
                        </td>
                        <td className="px-1.5 py-2 text-right text-ink-600">
                          {formatNumber(fila.comprobantes)}
                        </td>
                        <td className="px-1.5 py-2 text-right text-ink-600">
                          {formatNumber(fila.lineas)}
                        </td>
                        <td className="px-1.5 py-2 text-right text-ink-700">
                          {formatLegacyNumber(fila.cantidad)}
                        </td>
                        <td className="px-1.5 py-2 text-right font-semibold text-ink-900">
                          {formatLegacyCurrency(fila.total)}
                        </td>
                        <td className="px-1.5 py-2 text-right text-ink-700">
                          {formatLegacyCurrency(fila.descuento)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {porCliente.length > 0 ? (
                    <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                      <tr className="text-[13px] font-semibold text-ink-800">
                        <td className="px-1.5 py-2" colSpan={2}>
                          SUMA · {formatNumber(resumen?.clientes ?? 0)} cliente(s)
                        </td>
                        <td className="px-1.5 py-2 text-right">
                          {formatNumber(resumen?.lineas ?? 0)}
                        </td>
                        <td className="px-1.5 py-2 text-right">
                          {formatLegacyNumber(resumen?.cantidad ?? 0)}
                        </td>
                        <td className="px-1.5 py-2 text-right">
                          {formatCurrency(resumen?.total ?? 0)}
                        </td>
                        <td className="px-1.5 py-2 text-right">
                          {formatCurrency(resumen?.descuento ?? 0)}
                        </td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="space-y-3 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-ink-800">
                  Líneas vendidas ({formatNumber(resumen?.lineas ?? 0)})
                </h2>
                <span className="text-xs text-ink-500">
                  {etiquetaFamilia}
                  {filtros.solo_descuentos ? ' · sólo con descuento' : ''}
                </span>
              </div>

              {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

              {!consulta.isLoading ? (
                <>
                <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
                  <table className="w-full table-fixed border-collapse text-[13px]">
                    <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                      <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                        <th className="w-[4.75rem] px-1.5 py-2">Fecha</th>
                        <th className="w-[6rem] px-1.5 py-2">Comprobante</th>
                        <th className="w-[7.5rem] px-1.5 py-2">Cliente</th>
                        <th className="w-[6.5rem] px-1.5 py-2">Producto</th>
                        <th className="w-[4rem] px-1.5 py-2 text-right">Cant.</th>
                        <th className="w-[5.5rem] px-1.5 py-2 text-right">Total</th>
                        <th className="w-[5.5rem] px-1.5 py-2 text-right">Descuento</th>
                        <th className="w-[5.5rem] px-1.5 py-2">Grifero</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineas.length === 0 ? (
                        <tr className="border-t border-ink-100">
                          <td colSpan={8} className="px-1.5 py-10 text-center text-sm text-ink-500">
                            No hay ventas que cumplan los filtros.
                          </td>
                        </tr>
                      ) : null}

                      {paginacion.visibles.map((fila, indice) => (
                        <tr
                          key={`${fila.comprobante}-${indice}`}
                          className={cn(
                            'border-t border-ink-100 hover:bg-brand-50/30',
                            fila.anulado && 'text-ink-400',
                          )}
                        >
                          <td className="px-1.5 py-2">
                            <p className="truncate font-semibold">{formatDateValue(fila.fecha)}</p>
                            {fila.placa ? (
                              <p className="truncate text-[11px] text-ink-500">{fila.placa}</p>
                            ) : null}
                          </td>
                          <td className="px-1.5 py-2">
                            <p className="truncate font-medium text-ink-800">
                              {fila.comprobante ?? '—'}
                            </p>
                            <div className="mt-0.5 flex flex-wrap items-center gap-1">
                              <Badge
                                variant={tonoDelTipo(fila.tipo_documento)}
                                className="px-1.5 py-0 text-[10px]"
                              >
                                {fila.tipo_documento}
                              </Badge>
                              {fila.anulado ? (
                                <Badge variant="danger" className="px-1.5 py-0 text-[10px]">
                                  Anulado
                                </Badge>
                              ) : (
                                <span className="text-[10px] text-ink-500">{fila.estado}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-1.5 py-2">
                            <p className="truncate" title={fila.cliente ?? ''}>
                              {fila.cliente ?? '—'}
                            </p>
                            <p className="truncate text-[11px] text-ink-500">
                              {fila.documento ?? ''}
                            </p>
                          </td>
                          <td className="px-1.5 py-2">
                            <p className="truncate" title={fila.producto ?? ''}>
                              {fila.producto ?? '—'}
                            </p>
                            <p className="truncate text-[11px] text-ink-500">
                              {formatLegacyCurrency(fila.precio_unidad ?? 0)} / u.
                            </p>
                          </td>
                          <td className="px-1.5 py-2 text-right">
                            <p className="truncate">{formatLegacyNumber(fila.cantidad ?? 0)}</p>
                            <p className="truncate text-[11px] text-ink-500">
                              b {formatNumber(fila.subtotal ?? 0)}
                            </p>
                          </td>
                          <td className="px-1.5 py-2 text-right font-semibold">
                            {formatLegacyCurrency(fila.total ?? 0)}
                          </td>
                          <td className="px-1.5 py-2 text-right">
                            {Number(fila.descuento ?? 0) === 0
                              ? '—'
                              : formatLegacyCurrency(fila.descuento)}
                          </td>
                          <td className="px-1.5 py-2">
                            <p className="truncate text-ink-600" title={fila.grifero ?? ''}>
                              {fila.grifero ?? '—'}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {lineas.length > 0 ? (
                      <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                        <tr className="text-[13px] font-semibold text-ink-800">
                          <td className="px-1.5 py-2" colSpan={4}>
                            SUMA · {formatNumber(resumen?.comprobantes ?? 0)} comprobante(s)
                          </td>
                          <td className="px-1.5 py-2 text-right">
                            {formatLegacyNumber(resumen?.cantidad ?? 0)}
                          </td>
                          <td className="px-1.5 py-2 text-right">
                            {formatCurrency(resumen?.total ?? 0)}
                          </td>
                          <td className="px-1.5 py-2 text-right">
                            {formatCurrency(resumen?.descuento ?? 0)}
                          </td>
                          <td className="px-1.5 py-2" />
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>

                <Paginador {...paginacion} etiqueta="líneas" />
                </>
              ) : null}
            </CardContent>
          </Card>

          {porProducto.length > 0 ? (
            <Card>
              <CardContent className="space-y-3 p-4">
                <h2 className="text-sm font-semibold text-ink-800">Por producto</h2>

                <div className="max-h-72 overflow-auto rounded-card border border-ink-100">
                  <table className="w-full border-collapse text-[13px]">
                    <thead className="sticky top-0 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                      <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                        <th className="px-2 py-2">Producto</th>
                        <th className="w-[5rem] px-2 py-2 text-right">Líneas</th>
                        <th className="w-[6rem] px-2 py-2 text-right">Cantidad</th>
                        <th className="w-[6.5rem] px-2 py-2 text-right">Total</th>
                        <th className="w-[6.5rem] px-2 py-2 text-right">Descuento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {porProducto.map((fila) => (
                        <tr key={fila.producto_id} className="border-t border-ink-100">
                          <td className="px-2 py-2 text-ink-800">{fila.producto}</td>
                          <td className="px-2 py-2 text-right text-ink-600">
                            {formatNumber(fila.lineas)}
                          </td>
                          <td className="px-2 py-2 text-right text-ink-700">
                            {formatLegacyNumber(fila.cantidad)}
                          </td>
                          <td className="px-2 py-2 text-right font-semibold text-ink-900">
                            {formatLegacyCurrency(fila.total)}
                          </td>
                          <td className="px-2 py-2 text-right text-ink-700">
                            {formatLegacyCurrency(fila.descuento)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </>
      )}

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          Cada fila es una línea del comprobante y el pie suma cantidad, base, total y descuento. Las
          líneas de un comprobante <strong>anulado</strong> se ven con sus importes en cero (como en
          el panel anterior) y con su etiqueta; «sólo líneas con descuento» deja fuera las que no
          llevan descuento y también las anuladas. Los descuentos configurados por placa, consumo o
          recargo del módulo de playa pertenecen a la pantalla de descuentos activos, que aún no está
          portada.
        </span>
      </p>
    </div>
  );
}
